import cx from 'classnames';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, LinkProps, matchPath, useLocation, useMatch, useResolvedPath } from 'react-router-dom';

import { Avatar } from '../Avatar/Avatar';
import { Icon } from '../Icon/Icon';
import { IconNames } from '../Icon/IconNames';
import { Text } from '../Text/Text';

import Logo from '../../../assets/images/logo.svg';
import routes from '../../constants/routes';
import { useAppSelector, useAppThunkDispatch } from '../../hooks';
import { t } from '../../i18n';
import { dismissAnnouncement, selectAnnouncements } from '../../store/announcementSlice';
import { selectSecurity } from '../../store/securitySlice';
import { diffDays } from '../../utils/Date/Date';
import { getInitials } from '../../utils/Profile/profile';
import { Hamburger } from '../Hamburger/Hamburger';
import { Modal } from '../Modal/Modal';
import { useModal } from '../Modal/useModal';
import { SidebarBuildingList } from './SidebarBuildingList';
import { SidebarSubscription } from './SidebarSubscription';

interface SidebarLinkProps {
  to: LinkProps['to'];
  icon: IconNames;
  iconSelected: IconNames;
  title: string;
  renderCondition?: 'isAdmin' | 'isNotAdmin' | 'development';
  badge?: string;
  /** ISO datum (YYYY-MM-DD), po kterém se badge přestane zobrazovat – ať „NOVÉ" nevisí věčně. */
  badgeUntil?: string;
  /** 'accent' = oranžový badge (upoutání na stávající modul, např. TIP); default teal = nový modul. */
  badgeVariant?: 'default' | 'accent';
}

const items: SidebarLinkProps[] = [
  {
    to: routes.poll,
    icon: 'poll',
    iconSelected: 'pollSelected',
    title: t('Sidebar.poll'),
  },
  {
    to: routes.feed,
    icon: 'feed',
    iconSelected: 'feedSelected',
    title: t('Sidebar.feed'),
  },
  {
    to: routes.units,
    icon: 'unitsDefault',
    iconSelected: 'unitsSelected',
    title: t('Sidebar.units'),
    renderCondition: 'isAdmin',
  },
  {
    to: routes.building,
    icon: 'buildingDefault',
    iconSelected: 'buildingSelected',
    title: t('Sidebar.building'),
  },
  {
    to: routes.files,
    icon: 'filesDefault',
    iconSelected: 'filesSelected',
    title: t('Sidebar.files'),
  },
  {
    to: routes.registry,
    icon: 'registerDefault',
    iconSelected: 'registerSelected',
    title: t('Sidebar.registry'),
    renderCondition: 'isAdmin',
    badge: 'TIP',
    badgeUntil: '2026-10-01',
    badgeVariant: 'accent',
  },
  {
    to: routes.deadlines,
    icon: 'deadlinesDefault',
    iconSelected: 'deadlinesSelected',
    title: t('Sidebar.deadlines'),
    renderCondition: 'isAdmin',
    badge: 'NOVÉ',
    badgeUntil: '2027-01-01',
  },
  // Závady a podněty – pro všechny členy budovy (member i admin), bez renderCondition
  {
    to: routes.issues,
    icon: 'issuesDefault',
    iconSelected: 'issuesSelected',
    title: t('Sidebar.issues'),
    badge: 'NOVÉ',
    badgeUntil: '2027-01-01',
  },
  // Katalog služeb a partnerů – stejná route, jiný label pro předsedu a vlastníka (doc/services-catalog.md §2).
  // Položka pro PŘEDSEDY je do podpisu smluv s partnery dočasně zakomentovaná (stejně jako dřív Termíny):
  // sekce funguje přes přímou URL /otherServices/, aby šel finální stav s aktivovanými partnery
  // zkontrolovat před spuštěním. Před spuštěním pro předsedy odkomentovat (rozhodnutí Ondřeje 10/2026).
  // {
  //   to: routes.otherServices,
  //   icon: 'otherServicesDefault',
  //   iconSelected: 'otherServicesSelected',
  //   title: t('Sidebar.services'),
  //   renderCondition: 'isAdmin',
  //   badge: 'NOVÉ',
  //   badgeUntil: '2027-01-31',
  // },
  {
    to: routes.otherServices,
    icon: 'otherServicesDefault',
    iconSelected: 'otherServicesSelected',
    // Krátký label – „Výhody pro vlastníky NOVÉ" se do šířky sidebaru nevešlo (badge se ořezával).
    title: t('Sidebar.memberBenefits'),
    renderCondition: 'isNotAdmin',
    badge: 'NOVÉ',
    badgeUntil: '2027-01-31',
  },
  {
    to: routes.playground,
    icon: 'flag',
    iconSelected: 'flag',
    title: 'Playground',
    renderCondition: 'development',
  },
];

const handleSidebarLinkClick = () => {
  document.body.classList.remove('sidebar--is-opened');
};

/** Rozdělí adresu "Velkomoravská 2298, 68603 Staré Město" na ulici, město a PSČ (formát "xxx xx"). */
const splitAddress = (
  address: string | null | undefined
): { street: string; city: string | null; zip: string | null } => {
  if (!address) return { street: '', city: null, zip: null };
  const commaIndex = address.indexOf(',');
  if (commaIndex === -1) return { street: address, city: null, zip: null };

  const street = address.slice(0, commaIndex).trim();
  const rest = address.slice(commaIndex + 1).trim();
  const zipMatch = rest.match(/^(\d{3})\s?(\d{2})\s+(.+)$/);
  if (!zipMatch) return { street, city: rest, zip: null };
  return { street, city: zipMatch[3], zip: `${zipMatch[1]} ${zipMatch[2]}` };
};

/** Oddělí číslo domu (poslední token s číslicí), aby u dlouhých názvů ulic nikdy nezmizelo. */
const splitStreet = (street: string): { name: string; number: string | null } => {
  const lastSpace = street.lastIndexOf(' ');
  const number = lastSpace === -1 ? '' : street.slice(lastSpace + 1);
  if (!/\d/.test(number)) return { name: street, number: null };
  return { name: street.slice(0, lastSpace), number };
};

/** Badge položky, pokud ještě neexpiroval. Plné ISO s časem kvůli striktnímu parseru v Safari. */
const getActiveBadge = (item: SidebarLinkProps): string | undefined => {
  if (!item.badge) return undefined;
  const badgeExpired = item.badgeUntil ? new Date() >= new Date(`${item.badgeUntil}T00:00:00`) : false;
  return badgeExpired ? undefined : item.badge;
};

interface SidebarNavItemProps extends SidebarLinkProps {
  className?: string;
  onClick?: () => void;
  // Kotva pro onboarding callout (položka Jednotky) — ref jde přímo na <Link>,
  // obalový element by položce sebral margin přes pravidlo :last-child
  linkRef?: React.Ref<HTMLAnchorElement>;
}

const SidebarLink: React.FC<SidebarNavItemProps> = (props) => {
  const resolved = useResolvedPath(props.to);
  const match = useMatch({ path: `${resolved.pathname}/*` });
  const badge = getActiveBadge(props);

  const handleClick = () => {
    props.onClick?.();
    handleSidebarLinkClick();
  };

  return (
    <Link
      ref={props.linkRef}
      to={props.to}
      className={cx('SidebarNav__item', 'text-body-medium', match && 'SidebarNav__item--active', props.className)}
      onClick={handleClick}
    >
      <Icon className="SidebarNav__item-icon SidebarNav__item-icon--inactive" name={props.icon}></Icon>
      <Icon className="SidebarNav__item-icon SidebarNav__item-icon--active" name={props.iconSelected}></Icon>
      <span className="SidebarNav__item-link">{props.title}</span>
      {badge && (
        <span
          className={cx('SidebarNav__item-badge', props.badgeVariant === 'accent' && 'SidebarNav__item-badge--accent')}
        >
          {badge}
        </span>
      )}
    </Link>
  );
};

const useNavItems = (): SidebarLinkProps[] => {
  const isAdmin = useAppSelector(selectSecurity).ui.isAdmin;

  return items.filter((item) => {
    switch (item.renderCondition) {
      case 'development':
        return import.meta.env.DEV;
      case 'isAdmin':
        return isAdmin;
      case 'isNotAdmin':
        return !isAdmin;
      default:
        return true;
    }
  });
};

/**
 * Priority+ navigace: položky, které se nevejdou do dostupné výšky, se sbalí pod
 * rozbalovací položku „Další". Dostupná výška nezávisí na počtu vykreslených položek
 * (nav má flex: 1 v pevném layoutu), takže přepočet neosciluje. Výška slotu se měří
 * z DOM, protože se mění s max-height media queries.
 */
const SidebarNav = (): JSX.Element => {
  const navItems = useNavItems();
  const location = useLocation();
  const dispatch = useAppThunkDispatch();

  // Onboarding hint u položky Jednotky pro čerstvě registrované budovy — řízený
  // announcement systémem (kód onboarding-units-arrow), zmizí kliknutím na položku.
  // Na desktopu plovoucí callout vedle sidebaru (coach mark), pod hamburger
  // breakpointem decentní řádek pod položkou.
  const showUnitsHint = useAppSelector(selectAnnouncements).items.some(
    (item) => item.code === 'onboarding-units-arrow'
  );

  const unitsLinkRef = useRef<HTMLAnchorElement>(null);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 992px)').matches);
  const [calloutPosition, setCalloutPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 992px)');
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  const navRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navItems.length);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [flyoutPosition, setFlyoutPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    const measure = () => {
      const firstItem = nav.querySelector<HTMLElement>('.SidebarNav__item');
      if (!firstItem) return;

      const slotHeight = firstItem.getBoundingClientRect().height;
      const gap = parseFloat(window.getComputedStyle(firstItem).marginBottom) || 0;
      // Onboarding hint pod Jednotkami ukusuje z výšky pro položky
      const hintHeight = nav.querySelector<HTMLElement>('.SidebarNav__hint')?.getBoundingClientRect().height ?? 0;
      const available = nav.clientHeight - (hintHeight > 0 ? hintHeight + gap : 0);

      const fitsAll = navItems.length * (slotHeight + gap) - gap <= available;
      setVisibleCount(
        fitsAll ? navItems.length : Math.max(1, Math.floor((available - slotHeight) / (slotHeight + gap)))
      );
    };

    measure();
    // ResizeObserver kvůli změnám výšky navu bez resize okna, resize listener jako jistota
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [navItems.length, showUnitsHint]);

  // Pozice plovoucího calloutu: napravo od sidebaru, svisle na střed položky Jednotky.
  // Přepočet při resize; když položka spadne do overflow „Další", anchor zmizí a callout se schová.
  useLayoutEffect(() => {
    if (!showUnitsHint || !isDesktop) {
      setCalloutPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const link = unitsLinkRef.current;
      if (!link) {
        setCalloutPosition(null);
        return;
      }

      // Hrot šipky hned za textem „Jednotky" — na label vnímá uživatel líp než na celý řádek
      const label = link.querySelector<HTMLElement>('.SidebarNav__item-link');
      const linkRect = link.getBoundingClientRect();
      const labelRect = (label ?? link).getBoundingClientRect();
      if (labelRect.width === 0) {
        setCalloutPosition(null);
        return;
      }

      setCalloutPosition({
        left: labelRect.right + 6,
        top: linkRect.top + linkRect.height / 2,
      });
    };

    updatePosition();
    // Druhé měření po vykreslení — při překročení breakpointu se layout sidebaru
    // usadí až o snímek později a první měření by kotvilo na starou pozici
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showUnitsHint, isDesktop, visibleCount]);

  const hasOverflow = visibleCount < navItems.length;
  const visibleItems = hasOverflow ? navItems.slice(0, visibleCount) : navItems;
  const overflowItems = hasOverflow ? navItems.slice(visibleCount) : [];

  useEffect(() => {
    if (!hasOverflow) setIsMoreOpen(false);
  }, [hasOverflow]);

  // Pozice flyoutu: na desktopu vpravo vedle sidebaru zarovnaný na tlačítko,
  // na úzkých viewportech nad tlačítkem uvnitř šířky sidebaru. Panel se nejdřív
  // vykreslí skrytý (visibility: hidden), změří se a až pak se ukáže s animací.
  useLayoutEffect(() => {
    if (!isMoreOpen) {
      setFlyoutPosition(null);
      return;
    }

    const button = moreButtonRef.current;
    const flyout = flyoutRef.current;
    if (!button || !flyout) return;

    const margin = 8;
    const buttonRect = button.getBoundingClientRect();
    const sidebarRect = button.closest('.Sidebar')?.getBoundingClientRect();
    const { width, height } = flyout.getBoundingClientRect();
    const sidebarRight = sidebarRect?.right ?? buttonRect.right;

    if (sidebarRight + margin + width <= window.innerWidth) {
      setFlyoutPosition({
        left: sidebarRight + margin,
        top: Math.max(margin, Math.min(buttonRect.top - margin, window.innerHeight - height - margin)),
      });
    } else {
      setFlyoutPosition({
        left: (sidebarRect?.left ?? 0) + 16,
        top: Math.max(margin, buttonRect.top - height - margin),
      });
    }
  }, [isMoreOpen]);

  // Zavření flyoutu klikem mimo něj nebo Escape
  useEffect(() => {
    if (!isMoreOpen) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!flyoutRef.current?.contains(target) && !moreButtonRef.current?.contains(target)) {
        setIsMoreOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMoreOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMoreOpen]);

  const isItemActive = (item: SidebarLinkProps): boolean => {
    const path = typeof item.to === 'string' ? item.to : (item.to.pathname ?? '');
    return path !== '' && matchPath({ path: `${path.replace(/\/+$/, '')}/*` }, location.pathname) !== null;
  };

  const hasActiveOverflowItem = !isMoreOpen && overflowItems.some(isItemActive);
  const hasOverflowBadge = !isMoreOpen && overflowItems.some((item) => getActiveBadge(item) !== undefined);
  const closeMore = () => setIsMoreOpen(false);

  const handleItemClick = (item: SidebarLinkProps) => {
    closeMore();
    if (item.to === routes.units && showUnitsHint) {
      dispatch(dismissAnnouncement('onboarding-units-arrow'));
    }
  };

  return (
    <div ref={navRef} className="SidebarNav">
      {visibleItems.map((item) => (
        <Fragment key={item.title}>
          <SidebarLink
            {...item}
            linkRef={item.to === routes.units ? unitsLinkRef : undefined}
            onClick={() => handleItemClick(item)}
          />
          {item.to === routes.units && showUnitsHint && !isDesktop && (
            <div className="SidebarNav__hint">{t('Sidebar.unitsHint')}</div>
          )}
        </Fragment>
      ))}
      {showUnitsHint &&
        isDesktop &&
        calloutPosition &&
        createPortal(
          <div className="SidebarHintCallout" style={{ left: calloutPosition.left, top: calloutPosition.top }}>
            {/* Kreslená šipka: mělký oblouk, který do hrotu doběhne VODOROVNĚ — hrot
                natočený po tečně by opticky mířil nad položku, ne na ni */}
            <svg className="SidebarHintCallout__arrow" viewBox="0 0 64 30" aria-hidden="true">
              <path className="SidebarHintCallout__arrow-line" d="M60 20 C 46 26, 24 15, 9 15" fill="none" />
              <path className="SidebarHintCallout__arrow-head" d="M3 15 L13.5 10.5 L13.5 19.5 Z" />
            </svg>
            <div className="SidebarHintCallout__bubble">{t('Sidebar.unitsHint')}</div>
          </div>,
          document.body
        )}
      {hasOverflow && (
        <button
          ref={moreButtonRef}
          type="button"
          className={cx(
            'SidebarNav__item',
            'SidebarNav__more',
            'text-body-medium',
            hasActiveOverflowItem && 'SidebarNav__item--active',
            isMoreOpen && 'SidebarNav__more--open'
          )}
          aria-expanded={isMoreOpen}
          aria-haspopup="menu"
          onClick={() => setIsMoreOpen((value) => !value)}
        >
          <Icon className="SidebarNav__more-chevron" name="arrowDownSmall" />
          <span className="SidebarNav__item-link">{t('Sidebar.more')}</span>
          {hasOverflowBadge && <span className="SidebarNav__more-dot" />}
        </button>
      )}
      {isMoreOpen &&
        createPortal(
          <div
            ref={flyoutRef}
            className={cx('SidebarNavFlyout', flyoutPosition && 'SidebarNavFlyout--in')}
            style={flyoutPosition ?? { visibility: 'hidden' }}
          >
            {overflowItems.map((item) => (
              <SidebarLink
                key={item.title}
                {...item}
                className="SidebarNav__item--flyout"
                onClick={() => handleItemClick(item)}
              />
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

const LICENSE_EXPIRING_DAYS = 30;

export const Sidebar = (): JSX.Element => {
  const [isShown, toggle] = useModal();

  const profile = useAppSelector(selectSecurity).profile;
  const profileInitials = getInitials(profile?.firstName, profile?.lastName);
  const ui = useAppSelector(selectSecurity).ui;
  const buildings = useAppSelector(selectSecurity).buildings ?? [];

  const hasSubscription = ui.tokens?.filter((item) => item.type === 'building-subscription').length || 0;
  const hasPollVotes = ui.tokens?.filter((item) => item.type === 'poll-publish').length || 0;
  // konec licence vč. navázaných prodloužení — banner „končí za N dní" od 30 dní před koncem
  const licenseDaysLeft = ui.licenseValidTo ? diffDays(ui.licenseValidTo) : null;
  const addressParts = splitAddress(ui.address);
  const streetParts = splitStreet(addressParts.street);

  return (
    <div className="Sidebar">
      <div className="Sidebar__inner">
        <div className="SidebarHeader">
          <Link className="SidebarHeader__logo" to={routes.root} onClick={handleSidebarLinkClick}>
            <img src={Logo} alt={t('App.title')} />
          </Link>
          <Hamburger action={'close'} variant="light" />
          {/*
        <Link to={routes.notifications} className="SidebarHeader__notifications">
          <Icon name="notifications" />
        </Link>
        */}
        </div>
        <div
          className={cx('SidebarUnit', buildings.length > 1 && 'SidebarUnit--switchable')}
          onClick={buildings.length > 1 ? toggle : undefined}
        >
          <div className="SidebarUnit__info">
            <div className="SidebarUnit__street" title={ui.address ?? undefined}>
              <span className="SidebarUnit__street-name">{streetParts.name}</span>
              {streetParts.number && <span className="SidebarUnit__street-number">&nbsp;{streetParts.number}</span>}
            </div>
            {addressParts.city && (
              <div className="SidebarUnit__city">
                {addressParts.zip ? `${addressParts.city} · ${addressParts.zip}` : addressParts.city}
              </div>
            )}
          </div>
          {buildings.length > 1 && (
            <div className="SidebarUnit__switcher" role="button" aria-label={t('Sidebar.BuildingSelect.title')}>
              <Icon name="arrowDown" />
            </div>
          )}
        </div>
        <SidebarNav />

        {import.meta.env.VITE_DEMO === 'true' ? (
          <SidebarSubscription type="demo" />
        ) : (
          ui.isAdmin &&
          (ui.openOrder ? (
            <SidebarSubscription
              type="pending"
              orderId={ui.openOrder.id}
              proformaNumber={ui.openOrder.proformaNumber}
            />
          ) : !hasSubscription ? (
            <SidebarSubscription type={hasPollVotes ? 'warning' : 'error'} />
          ) : (
            licenseDaysLeft !== null &&
            licenseDaysLeft <= LICENSE_EXPIRING_DAYS && <SidebarSubscription type="expiring" days={licenseDaysLeft} />
          ))
        )}

        <div className={cx('SidebarUser')}>
          <Link to={routes.profile} className="SidebarUser__wrapper" onClick={handleSidebarLinkClick}>
            <Avatar initials={profileInitials} />

            <div className="SidebarUser__info ml-1">
              <Text as="div" size="body-small" weight="600" color="Neutral100">
                {profile?.firstName} {profile?.lastName}
              </Text>
              <Text as="div" size="body-small" weight="500" color="Neutral600">
                {profile?.email}
              </Text>
            </div>
          </Link>
        </div>
        <div className="SidebarUserActions">
          <Link
            to={routes.profile}
            className="SidebarUserActions__item SidebarUserActions__item--primary"
            onClick={handleSidebarLinkClick}
          >
            <Icon name="settings" className="SidebarUserActions__icon" />
            <span>{t('Sidebar.profile')}</span>
          </Link>
          <Link
            to={routes.logout}
            className="SidebarUserActions__item SidebarUserActions__item--icon"
            title={t('Sidebar.logout')}
            aria-label={t('Sidebar.logout')}
            onClick={handleSidebarLinkClick}
          >
            <Icon name="logout" className="SidebarUserActions__icon" />
          </Link>
        </div>

        <Modal isShown={isShown} onHide={toggle} size="small">
          <SidebarBuildingList buildings={buildings} onHide={toggle} />
        </Modal>
      </div>
    </div>
  );
};
